/**
 * Federated Search ver10 module
 * @author Andrew D.Laptev <a.d.laptev@gmail.com>
 * @see https://www.onvif.org/ver10/federatedsearch.wsdl
 */

import { Onvif, OnvifServices } from './onvif';
import Service from './service';
import {
  Capabilities,
  GetSearchResults,
  GetSearchResultsResponse,
  GetServiceFeatures,
  GetServiceFeaturesResponse,
  RegisterDatabase,
  RegisterDatabaseResponse,
  Search,
  SearchResponse,
  TermType,
} from './interfaces/federatedsearch';

/**
 * Federated Search service
 * @example
 * ```ts
 * const caps = await cam.federatedSearch.getServiceCapabilities();
 * const features = await cam.federatedSearch.getServiceFeatures({ inputCapabilities: {} });
 * ```
 */
export class FederatedSearch extends Service {
  constructor(onvif: Onvif, service: keyof OnvifServices) {
    super(onvif, service);
  }

  private static termTypeToBuild(term: TermType): unknown {
    return {
      $: { href: term.href },
      ...(term.name && { Name: term.name }),
      ...(term.description && { Description: term.description }),
      ...(term.term && {
        Term: FederatedSearch.oneOrMany(term.term, FederatedSearch.termTypeToBuild),
      }),
    };
  }

  private static oneOrMany<T>(items: T[] | undefined, builder: (item: T) => unknown) {
    if (!items?.length) {
      return undefined;
    }
    const built = items.map((item) => builder(item));
    return built.length === 1 ? built[0] : built;
  }

  private static registerDatabaseToBuild(database: RegisterDatabase) {
    return {
      ServiceID: database.serviceID,
      ...(database.recordingSearchInterfaceRegistration !== undefined && {
        RecordingSearchInterfaceRegistration: database.recordingSearchInterfaceRegistration,
      }),
      ...(database.supportedQFProfile && {
        SupportedQFProfile: FederatedSearch.termTypeToBuild(database.supportedQFProfile),
      }),
      ...(database.supportedMetadata && {
        SupportedMetadata: FederatedSearch.oneOrMany(
          database.supportedMetadata,
          (metadata) => metadata,
        ),
      }),
      ...(database.supportedExampleMediaTypes !== undefined && {
        SupportedExampleMediaTypes: database.supportedExampleMediaTypes,
      }),
      ...(database.supportedResultMediaTypes !== undefined && {
        SupportedResultMediaTypes: database.supportedResultMediaTypes,
      }),
      ...(database.supportedQueryTypes && {
        SupportedQueryTypes: FederatedSearch.oneOrMany(
          database.supportedQueryTypes,
          FederatedSearch.termTypeToBuild,
        ),
      }),
      ...(database.supportedExpressions && {
        SupportedExpressions: FederatedSearch.oneOrMany(
          database.supportedExpressions,
          FederatedSearch.termTypeToBuild,
        ),
      }),
      ...(database.extension && { Extension: database.extension }),
    };
  }

  /**
   * Returns the capabilities of the federated search service.
   */
  async getServiceCapabilities(): Promise<Capabilities> {
    const response = await this.request({
      GetServiceCapabilities: {},
    });
    return response.getServiceCapabilitiesResponse?.capabilities ?? {};
  }

  /**
   * Returns service features matching the requested capabilities.
   * @param options
   */
  async getServiceFeatures({ inputCapabilities }: GetServiceFeatures): Promise<GetServiceFeaturesResponse> {
    const response = await this.request({
      GetServiceFeatures: {
        InputCapabilities: inputCapabilities,
      },
    });
    return response.getServiceFeaturesResponse ?? {};
  }

  /**
   * Executes a federated search query.
   * @param options
   */
  async search({ inputQuery }: Search): Promise<SearchResponse> {
    const response = await this.request({
      Search: {
        InputQuery: inputQuery,
      },
    });
    return response.searchResponse ?? {};
  }

  /**
   * Returns results from a previous search query.
   * @param options
   */
  async getSearchResults({ results }: GetSearchResults): Promise<GetSearchResultsResponse> {
    const response = await this.request(
      {
        GetSearchResults: {
          Results: results,
        },
      },
      { array: ['resultItem'] },
    );
    return response.getSearchResultsResponse ?? {};
  }

  /**
   * Registers a database with the federated search service.
   * @param options
   */
  async registerDatabase(database: RegisterDatabase): Promise<RegisterDatabaseResponse> {
    const response = await this.request({
      RegisterDatabase: FederatedSearch.registerDatabaseToBuild(database),
    });
    return response.registerDatabaseResponse ?? {};
  }
}
